import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { getCurrentBusiness } from '../store/duck/businesses'
import {
  getKioskSegmentEnvId,
  initKioskAnalytics,
} from '../utils/kioskAnalytics'
import { KIOSK_MEMBERSHIP_ENROLLMENT_FLOW_ID_QUERY_PARAM } from '../utils/membershipQueryParams'
import { setSessionInfo } from '../utils/trackingProvider'

const getNormalizedPageContext = (
  pathname: string,
  search: string,
): { search: string; url: string } => {
  const normalizedSearchParams = new URLSearchParams(search)
  normalizedSearchParams.delete(KIOSK_MEMBERSHIP_ENROLLMENT_FLOW_ID_QUERY_PARAM)

  const normalizedSearch = normalizedSearchParams.toString()
  const normalizedUrl = new URL(window.location.href)

  normalizedUrl.pathname = pathname
  normalizedUrl.search = normalizedSearch

  return {
    search: normalizedSearch ? `?${normalizedSearch}` : '',
    url: normalizedUrl.toString(),
  }
}

const KioskAnalyticsWidget = () => {
  const currentBusiness = useSelector(getCurrentBusiness)
  const segmentEnvId = getKioskSegmentEnvId()
  const location = useLocation()
  const normalizedPageContext = getNormalizedPageContext(
    location.pathname,
    location.search,
  )

  useEffect(() => {
    if (!segmentEnvId || document.getElementById('segment-script')) {
      return
    }

    const segmentScript = document.createElement('script')
    segmentScript.id = 'segment-script'
    segmentScript.innerHTML = `
      try {
        !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics.SNIPPET_VERSION="4.13.1";
          analytics.load('${segmentEnvId}');
        }}();
      } catch(error) {
        console.error('Error caught in segment', error);
      }
    `

    if (process.env.NODE_ENV !== 'test') {
      document.head.appendChild(segmentScript)
    }
  }, [segmentEnvId])

  useEffect(() => {
    initKioskAnalytics()
  }, [])

  useEffect(() => {
    const businessType = currentBusiness?.omniChannel ? 'CVC' : '3P'

    setSessionInfo({
      appVersion: process.env.REACT_APP_SENTRY_RELEASE || '0.0.0',
      business: currentBusiness?.id,
      business_name: currentBusiness?.name,
      business_type: businessType,
      business_id: currentBusiness?.id,
    })
  }, [currentBusiness])

  useEffect(() => {
    const { analytics } = window as any

    if (!analytics?.page) {
      return
    }

    analytics.page(document.title, {
      app_source: 'kiosk',
      business_id: currentBusiness?.id,
      business_name: currentBusiness?.name,
      business_type: currentBusiness?.omniChannel ? 'CVC' : '3P',
      path: location.pathname,
      search: normalizedPageContext.search,
      title: document.title,
      url: normalizedPageContext.url,
    })
  }, [
    currentBusiness?.id,
    currentBusiness?.name,
    currentBusiness?.omniChannel,
    location.pathname,
    normalizedPageContext.search,
  ])

  return null
}

export default KioskAnalyticsWidget
